import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { authService } from '@/services/authService';
import { LockClosedIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { useTranslation } from '@/i18n/useTranslation';
import { normalizeAppUiLocale } from '@/i18n/appUiLocale';

interface ResetPasswordForm {
  password: string;
  confirm_password: string;
}

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { t, setLocale } = useTranslation('common');

  const token = searchParams.get('token');
  const userType = searchParams.get('type') || 'customer';
  const loginPath = userType === 'staff' ? '/auth/staff/login' : '/auth/customer/login';

  useEffect(() => {
    const lang = searchParams.get('lang');
    if (lang) {
      setLocale(normalizeAppUiLocale(lang));
    }
  }, [searchParams, setLocale]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ResetPasswordForm>();

  const password = watch('password');

  useEffect(() => {
    if (!token) {
      toast.error(t('auth.resetPasswordPage.invalidLinkToast'));
      navigate('/auth/forgot-password');
    }
  }, [token, navigate, t]);

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!token) {
      toast.error(t('auth.resetPasswordPage.invalidToken'));
      return;
    }

    if (data.password !== data.confirm_password) {
      toast.error(t('auth.resetPasswordPage.passwordsDoNotMatch'));
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await authService.resetPassword({
        token,
        password: data.password,
        confirm_password: data.confirm_password,
      });

      if (response.success) {
        setIsSuccess(true);
        toast.success(t('auth.resetPasswordPage.resetSuccessToast'));

        setTimeout(() => {
          navigate(loginPath);
        }, 3000);
      } else {
        toast.error(response.message || t('auth.resetPasswordPage.resetFailed'));
      }
    } catch (error: any) {
      console.error('Reset password error:', error);
      toast.error(error?.response?.data?.message || t('auth.resetPasswordPage.resetFailedRetry'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <LockClosedIcon className="h-6 w-6 text-green-600" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {t('auth.resetPasswordPage.successTitle')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('auth.resetPasswordPage.successSubtitle')}
          </p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <p className="text-sm text-green-700">
            {t('auth.resetPasswordPage.redirecting')}
          </p>
        </div>

        <div className="text-center">
          <Link
            to={loginPath}
            className="font-medium text-primary-600 hover:text-primary-500"
          >
            {t('auth.resetPasswordPage.goToLoginNow')}
          </Link>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <LockClosedIcon className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {t('auth.resetPasswordPage.invalidLinkTitle')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('auth.resetPasswordPage.invalidLinkSubtitle')}
          </p>
        </div>

        <div className="text-center">
          <Link
            to="/auth/forgot-password"
            className="font-medium text-primary-600 hover:text-primary-500"
          >
            {t('auth.resetPasswordPage.requestNewLink')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md w-full space-y-8">
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-100">
          <LockClosedIcon className="h-6 w-6 text-primary-600" />
        </div>
        <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
          {t('auth.resetPasswordPage.title')}
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          {t('auth.resetPasswordPage.subtitle')}
        </p>
      </div>

      <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label htmlFor="password" className="form-label">
            {t('auth.resetPasswordPage.newPassword')}
          </label>
          <div className="relative">
            <input
              {...register('password', {
                required: t('auth.validation.passwordRequired'),
                minLength: {
                  value: 8,
                  message: t('auth.resetPasswordPage.passwordMinLengthLong'),
                },
              })}
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              className={`input pr-10 ${errors.password ? 'input-error' : ''}`}
              placeholder={t('auth.resetPasswordPage.placeholderNewPassword')}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <EyeIcon className="h-5 w-5 text-gray-400" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="form-error mt-1">{errors.password.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="confirm_password" className="form-label">
            {t('auth.resetPasswordPage.confirmNewPassword')}
          </label>
          <div className="relative">
            <input
              {...register('confirm_password', {
                required: t('auth.resetPasswordPage.confirmPasswordRequired'),
                validate: (value) =>
                  value === password || t('auth.resetPasswordPage.passwordsDoNotMatch'),
              })}
              id="confirm_password"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              className={`input pr-10 ${errors.confirm_password ? 'input-error' : ''}`}
              placeholder={t('auth.resetPasswordPage.placeholderConfirmPassword')}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <EyeSlashIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <EyeIcon className="h-5 w-5 text-gray-400" />
              )}
            </button>
          </div>
          {errors.confirm_password && (
            <p className="form-error mt-1">{errors.confirm_password.message}</p>
          )}
        </div>

        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary w-full flex items-center justify-center"
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner className="w-5 h-5 mr-2" />
                {t('auth.resetPasswordPage.resettingPassword')}
              </>
            ) : (
              <>
                <LockClosedIcon className="w-5 h-5 mr-2" />
                {t('auth.resetPasswordPage.resetPassword')}
              </>
            )}
          </button>
        </div>

        <div className="text-center">
          <Link
            to={loginPath}
            className="font-medium text-primary-600 hover:text-primary-500"
          >
            {t('auth.forgotPasswordPage.backToLogin')}
          </Link>
        </div>
      </form>
    </div>
  );
};

export default ResetPassword;
